import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as MuiThemeProvider, type PaletteMode } from '@mui/material';
import { buildTheme } from '../theme/themeBuilder';
import GlobalStyles from '../theme/GlobalStyles';

export type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextValue {
  mode: ThemeMode;
  resolved: PaletteMode;
  setMode: (next: ThemeMode) => void;
}

const STORAGE_KEY = 'technouchet-theme';

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const systemDarkQuery = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;

function resolveMode(mode: ThemeMode): PaletteMode {
  return mode === 'auto' ? (systemDarkQuery() ? 'dark' : 'light') : mode;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => {
    if (typeof window === 'undefined') return 'dark';
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'auto') return saved;
    return 'auto';
  });

  const [resolved, setResolved] = useState<PaletteMode>(() => resolveMode(mode));

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore quota errors
    }
    setResolved(resolveMode(next));
  }, []);

  // Listen for system theme changes when in "auto" mode
  useEffect(() => {
    if (mode !== 'auto') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(systemDarkQuery() ? 'dark' : 'light');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [mode]);

  // Sync HTML attributes for non-MUI parts
  useEffect(() => {
    const root = document.documentElement;
    root.style.colorScheme = resolved;
    root.setAttribute('data-theme', resolved);
  }, [resolved]);

  const theme = useMemo(() => buildTheme(resolved), [resolved]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, resolved, setMode }),
    [mode, resolved, setMode]
  );

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        <GlobalStyles />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useThemeMode(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeMode must be used within ThemeProvider');
  return ctx;
}
